import { css, SerializedStyles } from '@emotion/react';
import {
    useHoneycombTheme,
    createResponsiveStyles,
    ResponsiveStyleValue,
    IconButtonSize,
    IconButtonColor,
} from '../../theming';

// Static styles
// Note: Icon button variables are derived from Global variables.
export const STATIC_STYLES: SerializedStyles = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--ib-size);
    height: var(--ib-size);
    border-radius: var(--ib-radius);
    color: var(--ib-icon-color);

    &:before {
        content: '';
        border-radius: inherit;
        transform: scaleX(0.75) scaleY(0.75) perspective(1px);
        backface-visibility: hidden;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background: transparent;
        opacity: var(--ib-hover-opacity);
        transition: transform 0.2s var(--hc-transition-easing-soft-bounce), background 0.15s ease-out;
    }

    &:focus-visible: before,
    &:hover:before {
        background: var(--ib-bg-color);
        transform: scaleX(1) scaleY(1) perspective(1px);
    }

    &:focus-visible:before {
        outline: var(--hc-focus-outline);
    }

    &[data-filled] {
        &:before {
            background: var(--ib-filled-background);
            opacity: 1;
            transform: scaleX(1) scaleY(1) perspective(1px);
        }

        &:focus-visible: before,
        &:hover:before {
            transform: scaleX(1.05) scaleY(1.05) perspective(1px);
        }
    }

    &[data-align-edge='start'] {
        margin-inline-start: calc(var(--ib-edge-adjustment) * -1);
    }

    &[data-align-edge='end'] {
        margin-inline-end: calc(var(--ib-edge-adjustment) * -1);
    }

    &[data-shape='circle'] {
        border-radius: calc(var(--ib-size) / 2);
    }

    &[data-shape='square'] {
        border-radius: 0;
    }

    [data-icon] {
        width: var(--ib-icon-size);
        height: var(--ib-icon-size);
        display: inline-block;
        fill: currentColor;
        position: relative;
    }
`;

export function useDynamicStyles(
    size: ResponsiveStyleValue<IconButtonSize>,
    color: IconButtonColor
): React.CSSProperties {
    const { theme } = useHoneycombTheme();

    return {
        '--ib-icon-color': `var(--ib-color-${color}-icon)`,
        '--ib-bg-color': `var(--ib-color-${color}-hover-bg)`,
        ...createResponsiveStyles(
            [
                {
                    value: size,
                    getStyles: (value: IconButtonSize) => {
                        return {
                            '--ib-size': `var(--ib-size-${value})`,
                            '--ib-radius': `var(--ib-radius-${value})`,
                            '--ib-icon-size': `var(--ib-icon-size-${value})`,
                        };
                    },
                },
            ],
            theme
        ),
    } as React.CSSProperties;
}
